/**
 * CoachPlaybook - PlayerForm Modal Component
 * Version: 0.5.145
 * Description: Mobile-optimized form with configurable optional fields
 * 
 * v0.5.54 Changes:
 * - Jersey templates passed as props from parent (consistent with PlayerCard)
 * - Instant updates when Team Settings changes
 */

import React, { useState, useEffect, useMemo } from 'react';
import { POSITIONS, isJerseyNumberAvailable, SKIN_TONES, HAIR_COLORS, HAIR_STYLES, SHOE_COLORS } from '../../utils/players';
import JerseyIcon, { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';
import { getSetting, setSetting } from '../../utils/db';

// Default field visibility settings
const DEFAULT_FIELD_SETTINGS = {
  showHeight: true,
  showGrade: true,
  showGraduationYear: true,
  showNotes: true
};

const ROSTER_FIELDS_KEY = 'rosterFieldSettings';

const PlayerForm = ({ 
  player, 
  players = [],
  jerseyTemplates: propsTemplates = [], 
  activeTemplateId: propsActiveId = 'default',
  onSave, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    jerseyNumber: '',
    position: 'PG',
    height: '',
    grade: '',
    graduationYear: '',
    notes: '',
    jerseyTemplateId: null,
    appearance: {
      skinTone: '#c68642',
      hairColor: '#2d1810',
      hairStyle: 'short',
      shoeColor: '#1f2937',
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);
  const [jerseyView, setJerseyView] = useState('back');
  const [fieldSettings, setFieldSettings] = useState(DEFAULT_FIELD_SETTINGS);
  const [showFieldSettings, setShowFieldSettings] = useState(false);

  // Use templates from props, fallback to empty array
  const jerseyTemplates = propsTemplates.length > 0 ? propsTemplates : [];
  
  // Get selected template from props templates
  const selectedTemplate = useMemo(() => {
    if (jerseyTemplates.length === 0) return DEFAULT_JERSEY_TEMPLATE;
    
    // If a specific template is selected, use it
    if (selectedTemplateId) {
      const found = jerseyTemplates.find(t => t.id === selectedTemplateId);
      if (found) return found;
    }
    
    // If player has assigned template, use it
    if (player?.jerseyTemplateId) {
      const playerTemplate = jerseyTemplates.find(t => t.id === player.jerseyTemplateId);
      if (playerTemplate) return playerTemplate;
    }
    
    // Prefer default jersey (isDefault), then activeTemplateId, then first
    const defaultJersey = jerseyTemplates.find(t => t.isDefault);
    if (defaultJersey) return defaultJersey;
    
    const active = jerseyTemplates.find(t => t.id === propsActiveId) || jerseyTemplates[0];
    return active || DEFAULT_JERSEY_TEMPLATE;
  }, [jerseyTemplates, selectedTemplateId, player?.jerseyTemplateId, propsActiveId]);

  // Load field settings and team logo (these can stay as IndexedDB loads)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const logo = await getSetting('teamLogo');
        if (logo) {
          setTeamLogo(logo);
        }
        
        // Load field visibility settings
        const savedFieldSettings = await getSetting(ROSTER_FIELDS_KEY);
        if (savedFieldSettings) {
          setFieldSettings({ ...DEFAULT_FIELD_SETTINGS, ...savedFieldSettings });
        }
        
        // Set initial selected template ID
        if (player?.jerseyTemplateId) {
          setSelectedTemplateId(player.jerseyTemplateId);
        } else if (propsActiveId) {
          setSelectedTemplateId(propsActiveId);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    loadSettings();
  }, [player]);

  // Populate form if editing
  useEffect(() => {
    if (player) {
      setFormData({
        firstName: player.firstName || '',
        lastName: player.lastName || '',
        jerseyNumber: player.jerseyNumber?.toString() || '',
        position: player.position || 'PG',
        height: player.height || '',
        grade: player.grade || '',
        graduationYear: player.graduationYear?.toString() || '',
        notes: player.notes || '',
        jerseyTemplateId: player.jerseyTemplateId || null,
        appearance: {
          skinTone: player.appearance?.skinTone || '#c68642',
          hairColor: player.appearance?.hairColor || '#2d1810',
          hairStyle: player.appearance?.hairStyle || 'short',
          shoeColor: player.appearance?.shoeColor || '#1f2937',
        }
      });
    }
  }, [player]);

  const saveFieldSettings = async (newSettings) => {
    setFieldSettings(newSettings);
    await setSetting(ROSTER_FIELDS_KEY, newSettings);
  };

  const toggleField = async (field) => {
    const newSettings = { ...fieldSettings, [field]: !fieldSettings[field] };
    await saveFieldSettings(newSettings);
  };

  const validateForm = async () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.jerseyNumber) {
      newErrors.jerseyNumber = 'Jersey number is required';
    } else {
      const num = parseInt(formData.jerseyNumber);
      if (isNaN(num) || num < 0 || num > 99) {
        newErrors.jerseyNumber = 'Must be between 0-99';
      } else {
        const available = isJerseyNumberAvailable(players, num, player?.id);
        if (!available) {
          newErrors.jerseyNumber = 'Number already taken';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) return;

    setIsSubmitting(true);

    const playerData = {
      ...formData,
      jerseyNumber: parseInt(formData.jerseyNumber),
      graduationYear: formData.graduationYear ? parseInt(formData.graduationYear) : null,
      jerseyTemplateId: selectedTemplate?.id || null,
      appearance: { ...formData.appearance }
    };

    await onSave(playerData);
    setIsSubmitting(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplateId(template.id);
    setFormData(prev => ({ ...prev, jerseyTemplateId: template.id }));
  };

  const handleAppearanceChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      appearance: { ...prev.appearance, [field]: value }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {player ? 'Edit Player' : 'Add New Player'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              * Required fields
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Field Settings Toggle */}
            <button
              type="button"
              onClick={() => setShowFieldSettings(!showFieldSettings)}
              className={`p-2 rounded-lg transition-colors ${showFieldSettings ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:bg-gray-100'}`}
              title="Configure fields"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Field Settings Panel */}
        {showFieldSettings && (
          <div className="px-4 md:px-6 py-3 bg-blue-50 border-b border-blue-100">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">Optional Fields (toggle to show/hide)</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'showHeight', label: 'Height' },
                { key: 'showGrade', label: 'Grade' },
                { key: 'showGraduationYear', label: 'Graduation Year' },
                { key: 'showNotes', label: 'Notes' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleField(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    fieldSettings[key]
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-500 border border-gray-300'
                  }`}
                >
                  {fieldSettings[key] ? '✓ ' : ''}{label}
                </button>
              ))}
            </div>
            <p className="text-xs text-blue-600 mt-2">These settings apply to all players and the roster view</p>
          </div>
        )}

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-5">
            
            {/* Jersey Preview Section */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <button type="button" onClick={() => setJerseyView('front')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${jerseyView === 'front' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border'}`}>
                  Front
                </button>
                <button type="button" onClick={() => setJerseyView('back')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${jerseyView === 'back' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border'}`}>
                  Back
                </button>
              </div>
              
              <div className="flex justify-center mb-4">
                <JerseyIcon
                  size={140}
                  number={formData.jerseyNumber || '00'}
                  view={jerseyView}
                  lastName={jerseyView === 'back' ? (formData.lastName?.toUpperCase() || 'NAME') : ''}
                  fillColor={selectedTemplate?.fillColor}
                  outlineColor={selectedTemplate?.outlineColor}
                  numberColor={selectedTemplate?.numberColor}
                  numberOutline={selectedTemplate?.numberOutline}
                  numberOutline2={selectedTemplate?.numberOutline2}
                  textColor={selectedTemplate?.textColor}
                  textOutline={selectedTemplate?.textOutline}
                  textOutline2={selectedTemplate?.textOutline2}
                  fontFamily={selectedTemplate?.fontFamily}
                  logoUrl={jerseyView === 'front' ? teamLogo : null}
                  numberY={selectedTemplate?.numberY ?? 55}
                  nameY={selectedTemplate?.nameY ?? 5}
                  numberSize={selectedTemplate?.numberSize ?? 100}
                  nameSize={selectedTemplate?.nameSize ?? 100}
                  jerseyBorderWeight={selectedTemplate?.jerseyBorderWeight ?? 12}
                  numberBorderWeight={selectedTemplate?.numberBorderWeight ?? 6}
                  numberBorderWeight2={selectedTemplate?.numberBorderWeight2 ?? 0}
                  nameBorderWeight={selectedTemplate?.nameBorderWeight ?? 4}
                  nameBorderWeight2={selectedTemplate?.nameBorderWeight2 ?? 0}
                />
              </div>
              
              {/* Template Selection */}
              {jerseyTemplates.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-2">Select Jersey Style</label>
                  <div className="flex gap-2 flex-wrap justify-center">
                    {jerseyTemplates.map(template => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleTemplateSelect(template)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                          selectedTemplate?.id === template.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div 
                          className="w-5 h-5 rounded border"
                          style={{ backgroundColor: template.fillColor, borderColor: template.outlineColor }}
                        />
                        <span className={`text-sm font-medium ${selectedTemplate?.id === template.id ? 'text-blue-700' : 'text-gray-700'}`}>
                          {template.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Required Fields: Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className={`w-full px-4 py-3 md:py-2 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className={`w-full px-4 py-3 md:py-2 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            {/* Required Fields: Jersey & Position Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Jersey # *</label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={formData.jerseyNumber}
                  onChange={(e) => handleChange('jerseyNumber', e.target.value)}
                  className={`w-full px-4 py-3 md:py-2 border rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.jerseyNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="23"
                />
                {errors.jerseyNumber && <p className="mt-1 text-sm text-red-600">{errors.jerseyNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Position *</label>
                <select
                  value={formData.position}
                  onChange={(e) => handleChange('position', e.target.value)}
                  className="w-full px-4 py-3 md:py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(POSITIONS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Optional Fields: Height & Grade Row */}
            {(fieldSettings.showHeight || fieldSettings.showGrade) && (
              <div className="grid grid-cols-2 gap-4">
                {fieldSettings.showHeight && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Height</label>
                    <input
                      type="text"
                      value={formData.height}
                      onChange={(e) => handleChange('height', e.target.value)}
                      className="w-full px-4 py-3 md:py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="6'2&quot;"
                    />
                  </div>
                )}
                {fieldSettings.showGrade && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Grade</label>
                    <select
                      value={formData.grade}
                      onChange={(e) => handleChange('grade', e.target.value)}
                      className="w-full px-4 py-3 md:py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select grade</option>
                      <option value="Freshman">Freshman</option>
                      <option value="Sophomore">Sophomore</option>
                      <option value="Junior">Junior</option>
                      <option value="Senior">Senior</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Optional Field: Graduation Year */}
            {fieldSettings.showGraduationYear && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Graduation Year</label>
                <input
                  type="number"
                  min="2024"
                  max="2035"
                  value={formData.graduationYear}
                  onChange={(e) => handleChange('graduationYear', e.target.value)}
                  className="w-full px-4 py-3 md:py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="2025"
                />
              </div>
            )}

            {/* Optional Field: Notes */}
            {fieldSettings.showNotes && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 md:py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Player strengths, areas to improve..."
                />
              </div>
            )}

            {/* Appearance Section */}
            <div className="bg-slate-800 rounded-xl p-4 space-y-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Appearance</h3>

              {/* Skin Tone */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Skin Tone</label>
                <div className="flex flex-wrap gap-2">
                  {SKIN_TONES.map(({ name, color }) => (
                    <button
                      key={color}
                      type="button"
                      title={name}
                      onClick={() => handleAppearanceChange('skinTone', color)}
                      className="rounded-full transition-transform hover:scale-110"
                      style={{
                        width: 28,
                        height: 28,
                        backgroundColor: color,
                        border: formData.appearance.skinTone === color
                          ? '2px solid #ffffff'
                          : '2px solid #475569',
                        boxShadow: formData.appearance.skinTone === color
                          ? '0 0 0 2px #3b82f6'
                          : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Hair Color */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Hair Color</label>
                <div className="flex flex-wrap gap-2">
                  {HAIR_COLORS.map(({ name, color }) => (
                    <button
                      key={color}
                      type="button"
                      title={name}
                      onClick={() => handleAppearanceChange('hairColor', color)}
                      className="rounded-full transition-transform hover:scale-110"
                      style={{
                        width: 28,
                        height: 28,
                        backgroundColor: color,
                        border: formData.appearance.hairColor === color
                          ? '2px solid #ffffff'
                          : '2px solid #475569',
                        boxShadow: formData.appearance.hairColor === color
                          ? '0 0 0 2px #3b82f6'
                          : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Hair Style */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Hair Style</label>
                <select
                  value={formData.appearance.hairStyle}
                  onChange={(e) => handleAppearanceChange('hairStyle', e.target.value)}
                  className="w-full px-4 py-2 bg-slate-700 text-gray-200 border border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {HAIR_STYLES.map(style => (
                    <option key={style} value={style}>
                      {style.charAt(0).toUpperCase() + style.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Shoe Color */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Shoe Color</label>
                <div className="flex flex-wrap gap-2">
                  {SHOE_COLORS.map(({ name, color }) => (
                    <button
                      key={color}
                      type="button"
                      title={name}
                      onClick={() => handleAppearanceChange('shoeColor', color)}
                      className="rounded-full transition-transform hover:scale-110"
                      style={{
                        width: 28,
                        height: 28,
                        backgroundColor: color,
                        border: formData.appearance.shoeColor === color
                          ? '2px solid #ffffff'
                          : '2px solid #475569',
                        boxShadow: formData.appearance.shoeColor === color
                          ? '0 0 0 2px #3b82f6'
                          : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer Buttons */}
        <div className="flex gap-3 p-4 md:p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (player ? 'Update Player' : 'Add Player')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerForm;
